// packages/ui/src/contexts/ThemeContext.tsx

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { ResolvedTheme, ThemeAppliedEventDetail, ThemeMode } from '@alamo/types';
import {
  THEME_APPLIED_EVENT,
  applyThemeMode,
  readStoredThemeMode,
  resolveTheme,
} from '@alamo/types';

export interface ThemeContextValue {
  /** Current preference: `light`, `dark`, or `auto` (follows the device). */
  mode: ThemeMode;
  /** Concrete theme in effect after resolving `auto` against the device. */
  resolvedTheme: ResolvedTheme;
  /**
   * Set the preference. Applies the `.dark` class and persists to
   * localStorage synchronously. Does NOT call the API — the toggle/selector
   * components own persistence to the backend to keep the provider decoupled
   * from the data layer.
   */
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Owns the theme mechanism: reads the persisted mode, applies the `.dark` class
 * on `<html>`, reacts to device `prefers-color-scheme` changes while in `auto`,
 * and stays in sync when the mode is applied elsewhere (e.g. AuthProvider on
 * login) via the `THEME_APPLIED_EVENT` window event.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredThemeMode());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(readStoredThemeMode()),
  );

  const setMode = useCallback((next: ThemeMode) => {
    const resolved = applyThemeMode(next);
    setModeState(next);
    setResolvedTheme(resolved);
  }, []);

  // Ensure the class matches the initial mode on mount (the anti-flash inline
  // script already set it pre-React; this reconciles React state with the DOM).
  useEffect(() => {
    setMode(readStoredThemeMode());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // In `auto`, follow live changes of the OS color scheme.
  useEffect(() => {
    if (mode !== 'auto') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setResolvedTheme(resolveTheme('auto'));
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, [mode]);

  // Sync when another actor applies the theme (AuthProvider server theme, other tab).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleApplied = (event: Event) => {
      const detail = (event as CustomEvent<ThemeAppliedEventDetail>).detail;
      if (!detail) return;
      setModeState(detail.mode);
      setResolvedTheme(resolveTheme(detail.mode));
    };

    window.addEventListener(THEME_APPLIED_EVENT, handleApplied);
    return () => window.removeEventListener(THEME_APPLIED_EVENT, handleApplied);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolvedTheme, setMode }),
    [mode, resolvedTheme, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Access the current theme mode, resolved theme, and setter. */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
